import React, { useCallback, useEffect } from 'react';
//Libs externas
import jwt_decode from "jwt-decode";
import { RootState } from 'store/ducks';
import { useSelector } from 'react-redux';
import { Route, useLocation, Routes, useNavigate } from 'react-router-dom';
//components
import Text from 'components/Text';
import Zenvia from 'components/Zenvia';
import Layout from 'components/Layout';
import { appInsights } from 'components/ApplicationInsights';
import { PageViewTracker } from 'components/ApplicationInsights/pageViewTracker';
//services
import UserService from 'services/user.service';
import AuthServices from 'core/authentication/authService';
import KeycloakPubServiceInstance from 'services/keycloakpubsub.service';
//utils
import keycloak from 'utils/keycloak';
import { isMobileDevice } from 'utils/ScreenUtils';
//interfaces
import { KeycloackProps } from './interface';
//Portal de Cotações
import Quotes from 'pages/Quotes';
import SavedInMoney from 'pages/Quotes/SavedInMoney';
import { MapProduct } from 'pages/Quotes/MapProduct';
import { ManualQuote } from 'pages/Quotes/ManualQuote';
import { BIPortal } from 'pages/Quotes/BIPortal';
import { ReportsPage } from 'pages/Quotes/ReportsPage';
import QuotesDetails from 'pages/Quotes/Details/Details';
import { PortalTraining } from 'pages/Quotes/PortalTraining';
import QuoteDashboard from 'pages/Quotes/Dashboard/dashboard';
//Customer Care
import Test from 'pages/Test';
import Orders from 'pages/Orders';
import Pipefy from 'pages/Pipefy';
import Police from 'pages/Police';
import { CTEs } from 'pages/CTEs';
import OauthPage from 'pages/OAuth';
import Protocols from 'pages/Tickets';
import Products from 'pages/Products';
import Customer from 'pages/Customer';
import Dashboard from 'pages/Dashboard';
import { Coverage } from 'pages/Coverage';
import UsersReport from 'pages/UsersReport';
import OpenProtocol from 'pages/OpenProtocol';
import CustomerData from 'pages/CustomerData';
import MissingLetters from 'pages/MissingLetters';
import EmployeeManager from 'pages/EmployeeManager';
import CustomerManager from 'pages/CustomerManager';
import { ReturnProcessor } from 'pages/ValidateProcess/ReturnProcessor';
import { ProcessCompletionLoss } from 'pages/ValidateProcess/ProcessCompletionLoss';


//context
import AppContextProvider from 'contexts/AppContextProvider';
import Skus from 'pages/Quotes/Skus';
import { PipefyLinkType } from 'models/User';

const PageNotFound = () => (
  <Text size={24} className="font-bold color-primary">
    Pagina não encontada
  </Text>
);

const PlatformRoutes: React.FC = (props: any) => {
  let user = {};

  const currentUser = useSelector((state: RootState) => state.user);
  const location = useLocation();
  const navigate = useNavigate();

  const _authServices = new AuthServices();
  const _userServices = new UserService();

  const tokenTimeout = 10 * 60 * 1000;
  const credentials = _authServices.credentials();
  const isMobile = isMobileDevice();

  const checkUserInfo = useCallback(async () => {
    if (currentUser && user !== currentUser && keycloak.authenticated) {
      keycloak.loadUserProfile().then(async (profile: any) => {
        const userProfile: KeycloackProps = profile as any;
        let ccare_type = userProfile.attributes.ccare_type?.[0] || undefined;
        const ccare_roles = userProfile.attributes.ccare_roles?.[0] || undefined;
        const currentUserRole = currentUser?.roles?.[0];
        if (!ccare_roles || !ccare_type || !ccare_roles?.toLowerCase().includes(currentUserRole?.toLowerCase() || "")) {
          await _userServices.setDefaultAtributtes(currentUser?.id || "");
          ccare_type = currentUser?.userType;
        }

        let pipefyToken = userProfile.attributes.pipefy_token?.[0] || await _userServices.getPipefyToken();
        if (pipefyToken && userProfile?.email && currentUser?.id && ccare_type && ccare_type.toLowerCase() === 'employee') {
          const decoded = jwt_decode(pipefyToken) as any;
          if (decoded?.user?.email !== userProfile?.email && currentUser.pipefyLink === PipefyLinkType.Member) {
            await _userServices.clearPipefyToken(currentUser.id);
            await _userServices.updateUserOnPipefy(userProfile?.email, decoded?.user?.email);
            pipefyToken = "";
          }
        }

        if (ccare_type && ccare_type.toLowerCase() === 'employee') {
          if (pipefyToken && pipefyToken !== '') {
            if (credentials !== undefined) {
              credentials.pipefy_token = pipefyToken;
              _authServices.save(credentials);
            }
          } else if (
            location.pathname !== '/oauth' &&
            currentUser.pipefyLink === PipefyLinkType.Member
          ) {
            window.location.href = '/oauth';
          }
        } else if (credentials !== undefined) {
          credentials.pipefy_token = pipefyToken;
          _authServices.save(credentials);
        }
      });

      user = currentUser;
    }
  }, [currentUser, user, keycloak.authenticated]);

  useEffect(() => {
    checkUserInfo();
  }, [checkUserInfo]);

  const initAuth = async () => {
    KeycloakPubServiceInstance.subscribe(async () => {
      keycloak.loadUserProfile().then(() => {
        appInsights.setAuthenticatedUserContext(
          keycloak.idTokenParsed?.sub as any,
          '',
          true
        );
      });
    });

    _authServices.checkSSO().then(() => {
      KeycloakPubServiceInstance.updateKeycloak(keycloak);
    });
  };

  const refreshToken = async () => {
    _authServices.refreshToken();
  };

  useEffect(() => {
    if (!keycloak.authenticated)
      initAuth();

    checkUserInfo();
    setInterval(refreshToken, tokenTimeout);
  }, []);

  const portalRoutes = ['cotacoes-dashboard', 'cotacoes-nova', 'gravado-no-money', 'de-para', 'relatorios-portal', 'baixa-manual', 'bi-portal'];
  const pathName = location.pathname.split("/")[1];

  const renderHelpLink = useCallback(() => {
    if (location.pathname === '/manutencao') return;

    if (portalRoutes.includes(pathName))
      return <PortalTraining />;

    if (currentUser?.userType !== 'supplier' && !isMobile)
      return <Zenvia />;

  }, [location, pathName, currentUser]);

  const isAuthenticatedTokenCustomerCare = credentials?.access_token;

  useEffect(() => {
    if (!isAuthenticatedTokenCustomerCare && keycloak?.profile?.email) {
      document.location.href = '/';
    }

    if (!isAuthenticatedTokenCustomerCare && !keycloak?.profile?.email) {
      setTimeout(() => {
        navigate(`${location.pathname}${location?.search}`);
      }, 1000);
    }
  }, [location.pathname]);

  return (
    <AppContextProvider>
      <PageViewTracker trackOnlyAuthenticated />
      <Layout {...props}>
        {keycloak.authenticated && isAuthenticatedTokenCustomerCare && (<>
          {renderHelpLink()}
          <Routes>
            <Route path="/" element={isMobileDevice() ? <Orders /> : <Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/teste" element={<Test />} />
            <Route path="/cotacoes-dashboard" element={<QuoteDashboard />} />
            <Route path="/cotacoes" element={<Quotes />} />
            <Route path="/cotacoes-nova/:id" element={<QuotesDetails />} />
            <Route path="/gravado-no-money/:id" element={<SavedInMoney />} />
            <Route path="/relatorios-portal" element={<ReportsPage />} />
            <Route path="/abrir-protocolo" element={<OpenProtocol />} />
            <Route path="/protocolos" element={<Protocols />} />
            <Route path="/cliente" element={<Customer />} />
            <Route path="/dados-cliente" element={<CustomerData />} />
            <Route path="/gerenciar-usuarios" element={<CustomerManager />} />
            <Route path="/gerenciar-usuarios-grupo" element={<EmployeeManager />} />
            <Route path="/pedidos" element={<Orders />} />
            <Route path="/forms" element={<Pipefy />} />
            <Route path="/produtos" element={<Products />} />
            <Route path="/cartas-falta" element={<MissingLetters />} />
            <Route path="/politicas" element={<Police />} />
            <Route path="/relatorio-usuarios" element={<UsersReport />} />
            <Route path="/oauth" element={<OauthPage />} />
            <Route path="/relatorio-testes-unitarios" element={<Coverage />} />
            <Route path="/cte" element={<CTEs />} />
            <Route path="/finalizacao-extravio" element={<ProcessCompletionLoss />} />
            <Route path="/aprovacao-devolucao" element={<ReturnProcessor />} />
            <Route path="/baixa-manual" element={<ManualQuote />} />
            <Route path="/bi-portal" element={<BIPortal />} />
            <Route path="/skus-prioritarios" element={<Skus />} />
            <Route element={<PageNotFound />} />
          </Routes>
        </>)}
      </Layout>
    </AppContextProvider>
  );
};
export default PlatformRoutes;